import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StarIcon from '@mui/icons-material/Star';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useContext, useState } from 'react';
import {
	ONETIME_PRODUCT_ID,
	PRODUCT_ID_LIBRARY,
	PRODUCT_ID_SINGLE,
} from '../../../config/constants';
import { formatPrice } from '../../../utils/numbers';
import { SnackbarContext } from '../../../providers/SnackBarProvider';
import { buttonTypographySx } from '../../../styles/patterns';
import {
	trackError,
	trackPatternMakerStep,
	ANALYTICS_EVENTS,
	ERROR_CONTEXTS,
} from '../../../services/analytics.service';

const priceDetails = [
	{
		name: 'Monthly',
		description: 'Create unlimited patterns with our monthly subscription',
		features: [
			'Create unlimited patterns',
			'Cancel anytime',
			'Perfect for new users',
			'Makes a great gift',
			'Ink saving patterns',
			'Lenticular patterns',
		],
		interval: 'month',
		intervalCopy: 'per month',
	},
	{
		name: 'Yearly',
		description: 'Save your money with our yearly subscription',
		features: [
			'Create unlimited patterns',
			'Cancel anytime',
			'Perfect for frequent users',
			'Makes a great gift',
			'Ink saving patterns',
			'Lenticular patterns',
		],
		interval: 'year',
		intervalCopy: 'per year',
	},
	{
		name: 'Single Pattern',
		description: 'Just want this pattern? You can buy it individually',
		features: [
			'Download pattern instantly',
			'Try before subscribing',
			'Perfect for occasional users',
			'Makes a great gift',
			'Ink saving patterns',
			'Lenticular patterns',
		],
		interval: null,
		intervalCopy: 'one time',
		product: PRODUCT_ID_SINGLE,
	},
	{
		name: 'Lifetime',
		new: true,
		description: 'One time payment for lifetime access',
		features: [
			'Create unlimited patterns',
			'No subscription fees',
			'Perfect for serious users',
			'Makes a great gift',
			'Ink saving patterns',
			'Lenticular patterns',
		],
		interval: null,
		intervalCopy: 'one time',
		product: ONETIME_PRODUCT_ID,
	},
	{
		name: 'Library Pattern',
		new: true,
		description: 'Purchase this pattern from our library',
		features: [
			'Download pattern instantly',
			'Try before subscribing',
			'Perfect for occasional users',
			'Makes a great gift',
			'Ink saving patterns',
		],
		interval: null,
		intervalCopy: 'one time',
		product: PRODUCT_ID_LIBRARY,
	},
];

export const PriceCard = (props) => {
	const { setSnackbar } = useContext(SnackbarContext);
	const [loading, setLoading] = useState(false);

	const {
		priceInfo,
		inProgress,
		escapeCallback,
		purchaseSubscription,
		purchaseSinglePattern,
		purchaseOneTime,
	} = props;
	const { data, id } = priceInfo;
	const [subscriptionInProgress, setSubscriptionInProgress] = inProgress;
	const details = priceDetails.find((priceDetail) => {
		//When the price data does not contain interval, find price details by product
		if (priceDetail.interval === null) {
			return priceDetail.product === data.product;
		}
		//Otherwise, find price details by interval
		return priceDetail.interval === data.interval;
	});

	const handleClick = async () => {
		// Subscription
		if (data.interval) {
			trackPatternMakerStep(
				ANALYTICS_EVENTS.PATTERN_MAKER_CHECKOUT_INITIATED,
				{ tier: 'subscription' },
			);
			showPurchaseInProgress();
			try {
				await purchaseSubscription(id);
			} catch (error) {
				trackError(ERROR_CONTEXTS.PRICE_CARD_SUBSCRIPTION, error, {
					tier: 'subscription',
				});
				showCheckoutError();
			}
		}
		// Lifetime Access
		else if (data.product === ONETIME_PRODUCT_ID) {
			trackPatternMakerStep(
				ANALYTICS_EVENTS.PATTERN_MAKER_CHECKOUT_INITIATED,
				{ tier: 'one_time' },
			);
			showPurchaseInProgress();
			try {
				await purchaseOneTime(id);
			} catch (error) {
				trackError(ERROR_CONTEXTS.PRICE_CARD_ONE_TIME, error, {
					tier: 'one_time',
				});
				showCheckoutError();
			}
		}
		// Single Pattern
		else {
			if (escapeCallback) {
				escapeCallback();
			} else {
				trackPatternMakerStep(
					ANALYTICS_EVENTS.PATTERN_MAKER_CHECKOUT_INITIATED,
					{ tier: 'single' },
				);
				showPurchaseInProgress();
				try {
					await purchaseSinglePattern(id);
				} catch (error) {
					trackError(ERROR_CONTEXTS.PRICE_CARD_SINGLE, error, {
						tier: 'single',
					});
					showCheckoutError();
				}
			}
		}
	};

	const showPurchaseInProgress = () => {
		setSubscriptionInProgress(true);
		setLoading(true);
		setSnackbar({
			text: 'Redirecting you to secure payment',
			severity: 'success',
		});
	};

	const showCheckoutError = () => {
		setSubscriptionInProgress(false);
		setLoading(false);
		setSnackbar({
			text: 'An error occurred. Please try again.',
			severity: 'error',
		});
	};

	return (
		<Grid
			size={{ xs: 12, sm: details.interval === null ? 12 : 6, md: 4 }}
			key={details.name}
			style={{ minWidth: '265px' }}
		>
			<Card
				sx={{
					p: 1,
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					borderRadius: 3,
					border: 1,
					borderColor: 'divider',
					boxShadow: 3,
					bgcolor: 'background.paper',
				}}
			>
				<CardContent sx={{ marginBottom: '-20px' }}>
					<Box
						sx={{
							mb: 1,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 2,
						}}
					>
						<Typography variant="h4">{details.name}</Typography>
						{details.new !== undefined && (
							<Chip
								icon={
									<StarIcon
										fontSize="small"
										style={{ marginBottom: '4px' }}
									/>
								}
								label="New"
								color="success"
								sx={{
									color: 'white',
									fontSize: '24px',
									paddingTop: '2px',
									paddingLeft: '2px',
								}}
							/>
						)}
					</Box>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'baseline',
							gap: 1,
						}}
					>
						<Typography component="h3" variant="h4">
							${formatPrice(data.unit_amount)}
						</Typography>
						<Typography component="h3" variant="h6">
							{details.intervalCopy}
						</Typography>
					</Box>
					<Divider
						sx={{ my: 2, opacity: 0.8, borderColor: 'divider' }}
					/>
					{details.features.map((feature) => (
						<Box
							key={feature}
							sx={{
								py: 0.5,
								display: 'flex',
								gap: 1.5,
								alignItems: 'center',
							}}
						>
							<CheckCircleRoundedIcon
								sx={{ width: 20, color: 'primary.main' }}
							/>
							<Typography variant="h6" component={'span'}>
								{feature}
							</Typography>
						</Box>
					))}
				</CardContent>
				<CardActions>
					<Button
						fullWidth
						variant={'contained'}
						color={'success'}
						disabled={subscriptionInProgress}
						onClick={handleClick}
						endIcon={
							loading && (
								<CircularProgress color="inherit" size={20} />
							)
						}
					>
						<Typography variant="h5" sx={buttonTypographySx}>
							Get started
						</Typography>
					</Button>
				</CardActions>
			</Card>
		</Grid>
	);
};
